<script setup>
import { computed } from 'vue'
import { Head, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import ProductCard from '@/Components/Public/ProductCard.vue'
import SeoCopy from '@/Components/Public/SeoCopy.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import EmptyResults from '@/Components/Public/EmptyResults.vue'
import ListYourBusinessCta from '@/Components/Public/ListYourBusinessCta.vue'
import { TOUR_CATEGORY_GROUP, ACTIVITY_CATEGORY_GROUP } from '@/lib/experienceCategories.js'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const props = defineProps({
  tours:      { type: Array,  default: () => [] },
  filters:    { type: Object, default: () => ({}) },
  // Full-taxonomy categories that currently have at least one bookable
  // listing (Public::ToursController#available_categories) — an empty
  // category never renders as a dead, always-empty chip.
  categories: { type: Array,  default: () => [] },
  base_path:  { type: String, default: '/tours' }
})

// Same two-way split as the vendor-portal Experiences form's category picker
// — grouped chip rows instead of one long undifferentiated row once the
// taxonomy grew past the original 7 categories.
const tourCategories     = computed(() => props.categories.filter(c => TOUR_CATEGORY_GROUP.includes(c)))
const activityCategories = computed(() => props.categories.filter(c => ACTIVITY_CATEGORY_GROUP.includes(c)))

const VIBES = ['family_friendly', 'romantic', 'relaxing', 'adrenaline', 'luxury', 'photo_worthy']

function applyFilter(key, value) {
  router.get(props.base_path, { ...props.filters, [key]: value }, { preserveScroll: true })
}
</script>

<template>
  <Head>
    <title>{{ t('public.tours.head_title') }}</title>
    <meta name="description" :content="t('public.tours.head_description')">
  </Head>

  <BrandHero kind="tour" :title="t('public.tours.title')">
    <p class="bhero-lede">{{ t('public.tours.lede') }}</p>
  </BrandHero>

  <section class="promise">
    <div class="promise__inner">
      <p class="promise__title">{{ t('public.experiences.private_promise.title') }}</p>
      <p class="promise__body">{{ t('public.experiences.private_promise.body') }}</p>
    </div>
  </section>

  <section class="listing">
    <div class="listing__inner">
      <div class="toolbar">
        <div class="filters filter-bar">
          <button :class="['chip', !filters.category && 'is-active']" @click="applyFilter('category', null)">{{ t('public.tours.filters.all') }}</button>
        </div>
        <select class="select" :value="filters.sort || 'price_asc'" @change="(e) => applyFilter('sort', e.target.value)">
          <option value="price_asc">{{ t('public.catalog.sort.price_asc') }}</option>
          <option value="price_desc">{{ t('public.catalog.sort.price_desc') }}</option>
          <option value="newest">{{ t('public.catalog.sort.newest') }}</option>
        </select>
      </div>
      <div v-if="tourCategories.length" class="filter-group">
        <span class="filter-group-label">{{ t('public.experiences.category_groups.tours') }}</span>
        <div class="filters filter-bar">
          <button v-for="cat in tourCategories" :key="cat" :class="['chip', filters.category === cat && 'is-active']" @click="applyFilter('category', cat)">{{ t(`public.experiences.categories.${cat}`) }}</button>
        </div>
      </div>
      <div v-if="activityCategories.length" class="filter-group">
        <span class="filter-group-label">{{ t('public.experiences.category_groups.activities') }}</span>
        <div class="filters filter-bar">
          <button v-for="cat in activityCategories" :key="cat" :class="['chip', filters.category === cat && 'is-active']" @click="applyFilter('category', cat)">{{ t(`public.experiences.categories.${cat}`) }}</button>
        </div>
      </div>
      <div class="filter-group">
        <span class="filter-group-label">{{ t('public.tours.filters.vibe_label') }}</span>
        <div class="filters filter-bar">
          <button :class="['chip', !filters.vibe && 'is-active']" @click="applyFilter('vibe', null)">{{ t('public.tours.filters.all') }}</button>
          <button v-for="v in VIBES" :key="v" :class="['chip', filters.vibe === v && 'is-active']" @click="applyFilter('vibe', v)">{{ t(`public.tours.vibes.${v}`) }}</button>
        </div>
      </div>

      <EmptyResults
        v-if="tours.length === 0"
        :title="t('public.tours.empty_title')"
        :sub="t('public.tours.empty_sub')"
        :clear-href="base_path"
        :clear-label="t('public.tours.empty_clear')"
      />

      <ul v-else class="grid">
        <li v-for="tr in tours" :key="tr.id">
          <ProductCard kind="tour" :item="tr" :href="`${base_path}/${tr.id}`" />
        </li>
      </ul>

      <ListYourBusinessCta />
    </div>
  </section>

  <SeoCopy section="experiences" />
</template>

<style scoped>
.bhero-lede { margin: 1rem 0 0; font-size: clamp(1.05rem, 2vw, 1.3rem); line-height: 1.5; max-width: 52ch; opacity: 0.92; }

.listing { --card-accent: var(--second); background: var(--bg); }
.listing__inner { max-width: 96rem; margin: 0 auto; padding: clamp(1.75rem, 4vw, 2.75rem) clamp(1.25rem, 4vw, 3rem) 4rem; }

.toolbar { display: flex; flex-wrap: wrap; gap: 0.75rem; justify-content: space-between; align-items: flex-start; }
.toolbar .filters { margin-bottom: 1.75rem; }
.select { padding: 0.5rem 0.85rem; border-radius: 999px; border: 1px solid var(--line); background: var(--surface); color: var(--ink); font-size: 0.9375rem; }

.filter-group { margin-bottom: 1.75rem; }
.filter-group-label { display: block; font-size: 0.8125rem; font-weight: 500; color: var(--inkSoft); margin-bottom: 0.5rem; letter-spacing: 0.03em; text-transform: uppercase; }
.filter-group .filters { margin-bottom: 0; }
.filters { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 1.75rem; }
.chip { padding: 0.5rem 1rem; border: 1px solid var(--line); background: var(--surface); border-radius: 999px; font-size: 0.9375rem; color: var(--inkSoft); cursor: pointer; }
.chip:hover { border-color: color-mix(in oklch, var(--gold) 70%, var(--line)); color: var(--ink); }
.chip.is-active { background: var(--night); color: var(--foam); border-color: var(--night); box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--gold) 45%, transparent); }

.grid { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr)); gap: 1.25rem; }
/* Private promise — every listing on this host is one group's day; the route is theirs. */
.promise { padding: clamp(1.5rem, 3vw, 2.25rem) 1.25rem 0; }
.promise__inner { max-width: 96rem; margin: 0 auto; display: grid; grid-template-columns: minmax(0, 18rem) minmax(0, 1fr); gap: 0.5rem 2.5rem; align-items: baseline; padding-bottom: clamp(1.25rem, 3vw, 2rem); border-bottom: 1px solid color-mix(in oklch, var(--gold, var(--warn)) 40%, var(--line)); }
.promise__title { margin: 0; font-family: var(--display); font-weight: 600; font-size: 1.55rem; line-height: 1.15; color: var(--ink); }
.promise__body { margin: 0; color: var(--inkSoft); line-height: 1.55; max-width: 64ch; }
@media (max-width: 48rem) { .promise__inner { grid-template-columns: 1fr; } }
</style>
